import { Badge } from "@/components/ui/badge";
import type { ColumnDef } from "@tanstack/react-table";
import { Pin } from "lucide-react";
import { DataTableColumnHeader } from "../table/header";
import { DataTableRowActions } from "./events.actions";

type Event = {
  id: string;
  title: string;
  description: string;
  start: Date;
  end: Date;
  allDay: boolean;
  imageUrl: string;
  calendarId: string;
  pinned: boolean;
  pinnedAt: Date | null;
  calendar: {
    id: string;
    name: string;
  };
};

export const columns: ColumnDef<Event>[] = [
  {
    accessorKey: "title",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Title" />
    ),
    cell: ({ row }) => {
      const event = row.original;
      return (
        <div className="flex items-center gap-2">
          {event.pinned && (
            <Pin className="h-4 w-4 text-yellow-500 fill-yellow-500" />
          )}
          <span className="font-medium">{event.title}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "start",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Start" />
    ),
    cell: ({ row }) => {
      const event = row.original;
      const date = new Date(event.start);
      if (event.allDay) {
        return <span>{date.toLocaleDateString()}</span>;
      }
      return (
        <div className="flex flex-col">
          <span className="text-sm">{date.toLocaleDateString()}</span>
          <span className="text-xs text-muted-foreground">
            {date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "end",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="End" />
    ),
    cell: ({ row }) => {
      const event = row.original;
      const date = new Date(event.end);
      if (event.allDay) {
        return <span>{date.toLocaleDateString()}</span>;
      }
      return (
        <div className="flex flex-col">
          <span className="text-sm">{date.toLocaleDateString()}</span>
          <span className="text-xs text-muted-foreground">
            {date.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "calendar",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Calendar" />
    ),
    cell: ({ row }) => {
      const calendar = row.original.calendar;
      return <Badge variant="secondary">{calendar.name}</Badge>;
    },
  },
  {
    accessorKey: "allDay",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="All Day" />
    ),
    cell: ({ row }) => {
      const allDay = row.getValue("allDay") as boolean;
      return allDay ? (
        <Badge variant="outline">All Day</Badge>
      ) : (
        <span className="text-muted-foreground text-sm">—</span>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
